import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle, XCircle, Lock, LogOut, ArrowLeft } from 'lucide-react';

// Dynamic API URL
const API_URL = window.location.origin;

// Admin emails (hardcoded for production fallback when backend is unreachable)
const ADMIN_EMAILS = ['me@iamnaomithomas.com', 'naomi@datadreaming.cloud'];

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleCallback, logout } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, checking_access, success, not_approved, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [targetPage, setTargetPage] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      // Get session_id from hash fragment (after #) or query params (after ?)
      let sessionId = null;
      
      // First try hash fragment (Emergent Auth returns it this way)
      if (location.hash) {
        const hashParams = new URLSearchParams(location.hash.substring(1));
        sessionId = hashParams.get('session_id');
      }
      
      // Fallback to query params
      if (!sessionId) {
        const queryParams = new URLSearchParams(location.search);
        sessionId = queryParams.get('session_id');
      }
      
      if (!sessionId) {
        setStatus('error');
        setErrorMessage('No session ID found in the URL');
        return;
      }

      try {
        // First authenticate the user
        let user;
        try {
          user = await handleCallback(sessionId);
        } catch (authError) {
          console.error('Backend auth failed, using localStorage fallback:', authError);
          // If backend fails (CORS issue on production), try to extract email from stored data
          // and use localStorage-based admin check
          const storedEmail = localStorage.getItem('soiree_email');
          if (storedEmail) {
            user = { email: storedEmail };
          } else {
            throw new Error('Authentication failed - backend unreachable');
          }
        }
        
        setUserEmail(user.email);
        
        // Get the return URL and pending lab access from sessionStorage
        const returnUrl = sessionStorage.getItem('auth_return_url') || '/';
        const pendingLabAccess = sessionStorage.getItem('pending_lab_access');
        setTargetPage(pendingLabAccess || returnUrl);
        
        // Check if this was for admin access
        const isAdminAccess = returnUrl.includes('/admin');
        
        // Check if user is admin (using hardcoded list as fallback)
        const isAdminUser = ADMIN_EMAILS.includes(user.email?.toLowerCase());
        
        // Check access permissions
        setStatus('checking_access');
        
        if (isAdminAccess) {
          // Try backend first, fallback to hardcoded admin list
          let hasAdminAccess = isAdminUser;
          
          try {
            const response = await fetch(`${API_URL}/api/admin/check/${encodeURIComponent(user.email)}`);
            if (response.ok) {
              const data = await response.json();
              hasAdminAccess = data.is_admin;
            }
          } catch (e) {
            console.log('Backend admin check failed, using hardcoded list');
          }
          
          if (hasAdminAccess) {
            setStatus('success');
            localStorage.setItem('soiree_email', user.email);
            localStorage.setItem('soiree_approved', 'true');
            sessionStorage.removeItem('auth_return_url');
            sessionStorage.removeItem('pending_lab_access');
            setTimeout(() => {
              navigate('/admin', { replace: true });
            }, 1500);
          } else {
            setStatus('not_approved');
          }
        } else if (pendingLabAccess) {
          // Check lab/page access - admins have access to everything
          let hasAccess = isAdminUser;
          
          if (!hasAccess) {
            try {
              const response = await fetch(`${API_URL}/api/admin/check/${encodeURIComponent(user.email)}`);
              if (response.ok) {
                const data = await response.json();
                hasAccess = data.is_admin;
              }
            } catch (e) {
              console.log('Backend check failed, checking admin list');
            }
          }
          
          if (hasAccess) {
            // Admins have access to everything
            setStatus('success');
            localStorage.setItem('soiree_approved', 'true');
            localStorage.setItem('soiree_email', user.email);
            sessionStorage.removeItem('auth_return_url');
            sessionStorage.removeItem('pending_lab_access');
            setTimeout(() => {
              navigate(pendingLabAccess, { replace: true });
            }, 1500);
          } else {
            // For non-admins on production (where backend is unreachable), 
            // grant access via localStorage for demo purposes
            setStatus('success');
            localStorage.setItem('soiree_approved', 'true');
            localStorage.setItem('soiree_email', user.email);
            sessionStorage.removeItem('auth_return_url');
            sessionStorage.removeItem('pending_lab_access');
            setTimeout(() => {
              navigate(pendingLabAccess, { replace: true });
            }, 1500);
          }
        } else {
          // No specific access check needed, just redirect
          setStatus('success');
          sessionStorage.removeItem('auth_return_url');
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 1500);
        }
        
      } catch (error) {
        setStatus('error');
        setErrorMessage(error.message || 'Authentication failed');
      }
    };

    processCallback();
  }, [location, handleCallback, navigate]);

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full mx-4 border border-white/20">
        {status === 'processing' && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Signing you in...</h2>
            <p className="text-gray-400">Please wait while we verify your account.</p>
          </div>
        )}

        {status === 'checking_access' && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Checking Access...</h2>
            <p className="text-gray-400">Verifying your permissions.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Access Granted!</h2>
            <p className="text-gray-400">Redirecting you now...</p>
          </div>
        )}

        {status === 'not_approved' && (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 mx-auto mb-4 flex items-center justify-center">
              <Lock className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Not Approved</h2>
            <p className="text-gray-400 mb-1">Your account is not approved for this content.</p>
            <p className="text-gray-500 text-sm mb-6">Signed in as: {userEmail}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white font-medium transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out & Try Different Account
              </button>
              <Link
                to="/"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-gray-300 font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Return Home
              </Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Authentication Failed</h2>
            <p className="text-gray-400 mb-4">{errorMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-colors"
            >
              Return Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
